// src/App.js
import React, { useState, useEffect, useCallback } from 'react';
import io from 'socket.io-client';
import Lobby from './Lobby';
import Room from './Room';
import GameScreen from './screens/GameScreen';
import LanguageSelector from './components/LanguageSelector';
import AdminScreen from './screens/AdminScreen';
import LegalPage from './screens/LegalPage';
import { useLanguage } from './contexts/LanguageContext';
// Cache buster: v2.1.8-foolproof-deploy-20251201-234948
import './App.css';

// Determine backend URL based on environment
const getBackendUrl = () => {
  // Check for environment variable first (useful for production builds)
  if (process.env.REACT_APP_BACKEND_URL) {
    return process.env.REACT_APP_BACKEND_URL;
  }
  
  // Development: localhost
  if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
    return 'http://localhost:3001';
  }
  
  // Local network (e.g., phone accessing via IP)
  if (window.location.hostname.startsWith('192.168.') || window.location.hostname.startsWith('10.')) {
    return `http://${window.location.hostname}:3001`;
  }
  
  // PRODUCTION: Use empty string for same-origin requests (most efficient)
  // This makes all API calls relative: /api/endpoint instead of https://domain.com/api/endpoint
  // This eliminates external DNS lookups and uses internal Docker networking
  return '';
};

const API_BASE = getBackendUrl();
console.log('🔗 Backend URL:', API_BASE);
const socket = io(API_BASE); // Connect to the WebSocket server

function App() {
  if (typeof window !== 'undefined') {
    if (window.location.pathname === '/legal') {
      return <LegalPage />;
    }
    if (window.location.pathname === '/admin') {
      return <AdminScreen />;
    }
  }
  return <MainApp />;
}

function MainApp() {
  const { t, currentLocale } = useLanguage();
  const [gameState, setGameState] = useState(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loginMode, setLoginMode] = useState('login'); // 'login' or 'register'
  const [currentView, setCurrentView] = useState('login'); // 'login', 'lobby', 'room', 'game'
  const [currentRoom, setCurrentRoom] = useState(null);
  const [userId, setUserId] = useState(null);
  const [authToken, setAuthToken] = useState(() => (typeof window !== 'undefined' ? localStorage.getItem('token') : null));
  const [challengeData, setChallengeData] = useState({ tier: 0 });

  const [isLeavingGame, setIsLeavingGame] = useState(false);

  const handleLogout = useCallback(async () => {
    try {
      await fetch(`${API_BASE}/api/auth/logout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
    } catch (error) {
      console.warn('Logout request failed (continuing anyway):', error);
    }

    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setIsLoggedIn(false);
    setUsername('');
    setPassword('');
    setConfirmPassword('');
    setLoginMode('login');
    setCurrentRoom(null);
    setGameState(null);
    setCurrentView('login');
    setUserId(null);
    setIsLeavingGame(false);
    setAuthToken(null);
    setChallengeData({ tier: 0 });
    socket.emit('logout');
    try {
      socket.disconnect();
    } catch (err) {
      console.warn('Socket disconnect failed (continuing):', err);
    }
    try {
      socket.connect();
    } catch (err) {
      console.warn('Socket reconnect failed (continuing):', err);
    }
  }, []);


  // Session restoration - runs once when the component first loads
  const fetchChallengeStatus = useCallback(async (overrideToken) => {
    try {
      const tokenToUse = overrideToken || authToken || localStorage.getItem('token');
      if (!tokenToUse) {
        return;
      }
      const res = await fetch(`${API_BASE}/api/challenges/status`, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${tokenToUse}`
        }
      });
      if (!res.ok) {
        throw new Error('Failed to load challenge status');
      }
      const status = await res.json();
      setChallengeData(status);
    } catch (error) {
      console.warn('Challenge status fetch failed:', error.message);
    }
  }, [authToken]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');

    if (token && storedUser) {
      const user = JSON.parse(storedUser);
      console.log(`Found stored session for: ${user.username}. Re-authenticating...`);
      
      setIsLoggedIn(true);
      setUsername(user.username);
      setUserId(user.id);
      setChallengeData(prev => ({ ...prev, tier: user.challengeTier || 0 }));
      setAuthToken(token);
      
      console.log('🔐 Sending authenticate event with token');
      socket.emit('authenticate', { token, locale: currentLocale });
      
      console.log('🔄 Setting view to lobby, waiting for reconnection...');
      setCurrentView('lobby');
      fetchChallengeStatus(token);
    }
  }, [fetchChallengeStatus, currentLocale]);

  useEffect(() => {
    socket.on('connect', () => {
      console.log('Connected to server!');
      console.log('Socket ID:', socket.id);
      // A reconnect (network blip, server restart) arrives as a new socket that
      // the server does not know yet; re-authenticate so the player keeps
      // their seat instead of being timed out.
      const storedToken = localStorage.getItem('token');
      if (storedToken) {
        socket.emit('authenticate', { token: storedToken, locale: currentLocale });
      }
    });

    socket.on('gameUpdate', (newGameState) => {
      console.log('Game update received:', newGameState);
      
      if (currentView !== 'game' && currentView !== 'lobby' && !isLeavingGame) {
        console.log('Session restoration: switching to game view');
        setCurrentView('game');
      }
      
      if (newGameState.roomId && (!currentRoom || currentRoom.roomId !== newGameState.roomId)) {
        console.log('Setting currentRoom from gameUpdate:', newGameState.roomId);
        setCurrentRoom({ roomId: newGameState.roomId });
      }
      
      const transformedState = {
        ...newGameState,
        userHand: newGameState.hand || [],
        messages: newGameState.messages || [],
        trickCards: newGameState.trickCards || [null, null, null, null],
        trickWinner: newGameState.trickWinner !== undefined ? newGameState.trickWinner : -1
      };
      setGameState(transformedState);
    });

    socket.on('challengeResult', (payload) => {
      console.log('Challenge result received:', payload);
      const winMessage = t('challenge.win', { tier: payload?.tier, label: payload?.label || '' }) || `Challenge ${payload?.tier} complete! ${payload?.label || ''}`;
      const loseMessage = t('challenge.lose', { tier: payload?.tier, label: payload?.label || '' }) || `Challenge ${payload?.tier || ''} failed.`;
      alert((payload?.success ? winMessage : loseMessage).trim());
      setGameState(null);
      setCurrentRoom(null);
      setCurrentView('lobby');
      if (payload?.newTier !== undefined) {
        setChallengeData(prev => ({ ...prev, tier: payload.newTier }));
        const storedUserRaw = localStorage.getItem('user');
        if (storedUserRaw) {
          try {
            const storedUser = JSON.parse(storedUserRaw);
            storedUser.challengeTier = payload.newTier;
            localStorage.setItem('user', JSON.stringify(storedUser));
          } catch (err) {
            console.warn('Failed to update stored user tier', err);
          }
        }
      }
      fetchChallengeStatus();
    });

    socket.on('challengeError', (payload) => {
      const message = payload?.message || 'Challenge could not be started.';
      const localized = t('challenge.error', { message }) || message;
      alert(localized);
      fetchChallengeStatus();
    });

    
    socket.on('leftGame', (data) => {
      console.log('✅ Successfully left game:', data);
      setIsLeavingGame(false);
      setCurrentRoom(null);
      setGameState(null);
      setCurrentView('lobby');
    });
    
    socket.on('roomDeleted', (data) => {
      console.log('🗑️ Room deleted:', data);
      setCurrentRoom(null);
      setGameState(null);
      setCurrentView('lobby');
    });
    
    socket.on('authSuccess', (data) => {
      console.log('🔐 AUTH SUCCESS received:', data);
      
      if (data.activeRoom) {
        console.log('🎮 Rejoining active room:', data.activeRoom);
        setCurrentRoom(data.activeRoom);
        // A finished room here is a game abandoned while the player was away;
        // the game view shows the abandoned notice.
        if (data.activeRoom.gameInProgress || data.activeRoom.status === 'finished') {
          console.log('🎮 Going to game view via authSuccess');
          setCurrentView('game');
        } else {
          console.log('🎮 Going to room view via authSuccess');
          setCurrentView('room');
        }
      } else {
        console.log('🏠 No active room, going to lobby');
        setCurrentView('lobby');
      }
    });

    socket.on('authError', (error) => {
      console.error('Authentication failed:', error);
      handleLogout();
    });

    socket.on('roomUpdate', (roomInfo) => {
      console.log('Room update received:', roomInfo);
      setCurrentRoom(roomInfo);
      if (currentView !== 'game') {
        setCurrentView('room');
      }
    });

    return () => {
      socket.off('gameUpdate');
      socket.off('connect');
      socket.off('authSuccess');
      socket.off('authError');
      socket.off('roomUpdate');
      socket.off('challengeResult');
      socket.off('challengeError');
      socket.off('leftGame');
      socket.off('roomDeleted');
    };
  }, [currentView, currentRoom, isLeavingGame, handleLogout, fetchChallengeStatus, t, currentLocale]);

  useEffect(() => {
    if (currentView !== 'lobby') {
      return;
    }
    const emitJoinLobby = () => {
      console.log('🌐 Emitting joinLobby');
      socket.emit('joinLobby');
    };
    if (socket.connected) {
      emitJoinLobby();
    } else {
      socket.once('connect', emitJoinLobby);
    }

    return () => {
      socket.off('connect', emitJoinLobby);
    };
  }, [currentView]);

  const handleLogin = async (e) => {
    e.preventDefault();

    // Validate password confirmation for registration
    if (loginMode === 'register' && password !== confirmPassword) {
      alert('Passwords do not match!');
      return;
    }

    // Declared outside the try so the catch below can name the target it
    // failed to reach; a const inside the try is not in scope there.
    const endpoint = loginMode === 'login' ? '/api/auth/login' : '/api/auth/register';

    try {
      const res = await fetch(`${API_BASE}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password })
      });

      if (res.ok) {
        const data = await res.json();
        if (loginMode === 'login') {
          // Store both token and user object to localStorage
          localStorage.setItem('token', data.token);
          localStorage.setItem('user', JSON.stringify(data.user));
          setIsLoggedIn(true);
          setUserId(data.user?.id || socket.id);
          setAuthToken(data.token);
          setChallengeData(prev => ({ ...prev, tier: data.user?.challengeTier || 0 }));
          fetchChallengeStatus(data.token);

          // Authenticate the socket connection with the token
          if (socket.connected) {
            socket.emit('authenticate', { token: data.token, locale: currentLocale });
          } else {
            // Wait for connection and then authenticate
            socket.once('connect', () => {
              socket.emit('authenticate', { token: data.token, locale: currentLocale });
            });
          }

          setCurrentView('lobby');
        } else {
          // Registration successful, switch to login
          alert('Registration successful! Please log in.');
          setLoginMode('login');
          setPassword('');
          setConfirmPassword('');
        }
      } else {
        // Not every failure comes back shaped like {msg}. The rate limiter
        // answers {error}, and nginx answers with an HTML page, so reading
        // .msg alone turned real errors into a blank "Authentication failed"
        // and threw away the only clue about what actually went wrong.
        const raw = await res.text();
        let detail = raw;
        try {
          const parsed = JSON.parse(raw);
          detail = parsed.msg || parsed.error || raw;
        } catch (e) {
          detail = `${res.status} ${res.statusText}`;
        }
        console.error('Auth failed:', res.status, API_BASE + endpoint, raw);
        alert(`Authentication failed (${res.status}): ${detail}`);
      }
    } catch (error) {
      // A throw here is a network-level failure -- wrong API host, DNS,
      // TLS or CORS -- not a rejected password. Say which.
      console.error('Auth request never completed:', error, 'target:', API_BASE + endpoint);
      alert(`Could not reach the server at ${API_BASE || window.location.origin}: ${error.message}`);
    }
  };

  const handleGuestLogin = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/auth/guest`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}) // express.json() expects a JSON payload when header is set
      });

      if (res.ok) {
        const data = await res.json();

        // Store both token and user object to localStorage
          localStorage.setItem('token', data.token);
          localStorage.setItem('user', JSON.stringify(data.user));
          setIsLoggedIn(true);
          setUsername(data.user.username);
          setUserId(data.user?.id || socket.id);
          setAuthToken(data.token);
          setChallengeData(prev => ({ ...prev, tier: data.user?.challengeTier || 0 }));
          fetchChallengeStatus(data.token);

        // Authenticate the socket connection with the token
        if (socket.connected) {
          socket.emit('authenticate', { token: data.token, locale: currentLocale });
        } else {
          // Wait for connection and then authenticate
          socket.once('connect', () => {
            socket.emit('authenticate', { token: data.token, locale: currentLocale });
          });
        }

        setCurrentView('lobby');
      } else {
        const error = await res.json();
        alert(error.msg || 'Guest login failed');
      }
    } catch (error) {
      console.error('Guest login error:', error);
      alert('Guest login failed');
    }
  };

  const handleCreateRoom = (roomId, roomInfo) => {
    console.log('handleCreateRoom - roomInfo:', roomInfo);
    console.log('handleCreateRoom - socket.id:', socket.id);
    setCurrentRoom(roomInfo);
    setCurrentView('room');
  };

  const handleJoinRoom = (roomId) => {
    // Room joining is handled by Lobby component
    // Just listen for successful join
    socket.once('roomUpdate', (roomInfo) => {
      setCurrentRoom(roomInfo);
      setCurrentView('room');
    });
  };

  const handleStartChallenge = useCallback((challengeTier) => {
    socket.emit('startChallenge', { tier: challengeTier, locale: currentLocale });
  }, [currentLocale]);

  const handleLeaveRoom = () => {
    setCurrentRoom(null);
    setCurrentView('lobby');
  };

  const handleGameStart = (gameStartData) => {
    console.log('Game started data:', gameStartData);
    setCurrentView('game');
    // Wait for individual gameUpdate which will have the proper structure
  };

  const handleLeaveGame = () => {
    // Show confirmation dialog
    const confirmLeave = window.confirm(
      `Are you sure you want to leave the game?

You will be replaced with a bot and cannot rejoin this game.`
    );
    
    if (confirmLeave) {
      // Set leaving state to prevent race conditions
      setIsLeavingGame(true);
      // Emit leave game event and wait for server confirmation
      socket.emit('leaveGame', { roomId: currentRoom?.roomId });
      // Don't change view immediately - wait for leftGame event
    }
  };

  // Handler for returning to lobby after game completion (no warning needed)
  const handleReturnToLobby = () => {
    console.log('Returning to lobby after game completion');
    setGameState(null);
    setCurrentRoom(null);
    setCurrentView('lobby');
  };

  // Render based on current view
  if (currentView === 'login' || !isLoggedIn) {
    return (
      <div className="app lobby-background">
        <div className="login-container">
          <LanguageSelector 
            className="language-selector-login"
          />
          <h1>Kwajongen</h1>
          <form onSubmit={handleLogin}>
            <h2>{loginMode === 'login' ? 'Login' : 'Register'}</h2>
            <div>
              <input
                type="text"
                placeholder="Username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
              />
            </div>
            <div>
              <input
                type="password"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>
            {loginMode === 'register' && (
              <div>
                <input
                  type="password"
                  placeholder="Confirm Password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                />
              </div>
            )}
            <button type="submit" className="primary-btn">
              {loginMode === 'login' ? 'Login' : 'Register'}
            </button>
            
            <div className="divider">
              <span>or</span>
            </div>

            <button type="button" className="guest-btn" onClick={handleGuestLogin}>
              Continue as Guest
            </button>

            <p>
              {loginMode === 'login' ? "Don't have an account? " : "Already have an account? "}
              <button
                type="button"
                className="link-btn"
                onClick={() => {
                  setLoginMode(loginMode === 'login' ? 'register' : 'login');
                  setPassword('');
                  setConfirmPassword('');
                }}
              >
                {loginMode === 'login' ? 'Register' : 'Login'}
              </button>
            </p>
          </form>
        </div>
      </div>
    );
  }
  
  // Lobby Screen
  if (currentView === 'lobby') {
    return (
      <div className="app lobby-background">
        <Lobby 
          socket={socket}
          username={username}
          onCreateRoom={handleCreateRoom}
          onJoinRoom={handleJoinRoom}
          onLogout={handleLogout}
          tiers={challengeData}
          onStartChallenge={handleStartChallenge}
        />
      </div>
    );
  }
  
  // Room Screen
  if (currentView === 'room' && currentRoom) {
    // Wait for socket to be connected
    if (!socket.id) {
      return <div className="app"><div>Connecting to server...</div></div>;
    }
    
    return (
      <div className="app room-background">
        <Room
          socket={socket}
          roomInfo={currentRoom}
          currentUserId={userId}
          onLeaveRoom={handleLeaveRoom}
          onGameStart={handleGameStart}
        />
      </div>
    );
  }

  // Game Screen
  if (currentView === 'game' && gameState) {
    return (
      <div className="app">
        <GameScreen
          gameState={gameState}
          socket={socket}
          currentRoom={currentRoom}
          onLeaveGame={handleLeaveGame}
          onReturnToLobby={handleReturnToLobby}
          isLeavingGame={isLeavingGame}
        />
      </div>
    );
  }
  
  // Default fallback
  return <div>Loading...</div>;
}


export default App;
